import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { contactTranslations } from '../data/translations/contactTranslations';

const EMAIL_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;

export default function Contact() {
  const { language } = useLanguage();
  const t = contactTranslations[language].contact;

  const [formData, setFormData] = useState({ name: '', institution: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error

  const validate = (data) => {
    const e = {};
    if (!data.name.trim())                  e.name    = t.form.errors.nameRequired;
    if (!data.email.trim())                 e.email   = t.form.errors.emailRequired;
    else if (!EMAIL_REGEX.test(data.email)) e.email   = t.form.errors.emailInvalid;
    if (!data.message.trim())               e.message = t.form.errors.messageRequired;
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleBlur = (e) => {
    setTouched(prev => ({ ...prev, [e.target.name]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate(formData);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched({ name: true, email: true, message: true });
      return;
    }

    setStatus('submitting');
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_KEY,
          from_name: 'Intelimed Website Contact Form',
          name: formData.name,
          institution: formData.institution,
          email: formData.email,
          message: formData.message,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setStatus('success');
        setFormData({ name: '', institution: '', email: '', message: '' });
        setTouched({});
      } else {
        throw new Error(data.message || 'Error');
      }
    } catch {
      setStatus('error');
      setErrors(prev => ({ ...prev, submit: t.form.errors.submitError }));
    }
  };

  const fieldClass = (name) =>
    `w-full bg-background border rounded-xl px-5 py-4 text-xs outline-none transition-all ${
      errors[name] && touched[name]
        ? 'border-red-400 focus:border-red-400'
        : 'border-border focus:border-secondary'
    }`;

  return (
    <div className="bg-background text-on-surface font-body" id="contact">
      <section className="bg-background py-24 px-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/5 blur-[100px] rounded-full translate-x-1/2 -translate-y-1/2"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">

            {/* Left info box */}
            <div className="bg-surface border border-border rounded-[2.5rem] p-8 md:p-14 shadow-2xl">
              <h2 className="font-headline text-3xl md:text-4xl font-extrabold text-primary mb-6 tracking-tight">
                {t.title}
              </h2>
              {t.subtitle && (
                <p className="font-headline text-xl font-bold text-primary mb-3">{t.subtitle}</p>
              )}
              <p className="opacity-60 text-base mb-10">{t.description}</p>

              <div className="space-y-6 mt-12">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center text-secondary">
                    <span className="material-symbols-outlined text-lg" aria-hidden="true">location_on</span>
                  </div>
                  <span className="text-xs font-bold opacity-80 uppercase tracking-widest">{t.location}</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center text-secondary">
                    <span className="material-symbols-outlined text-lg" aria-hidden="true">mail</span>
                  </div>
                  <span className="text-xs font-bold opacity-80 uppercase tracking-widest">{t.email}</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
                    <svg className="w-4 h-4 fill-secondary" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </div>
                  <a
                    href="https://www.linkedin.com/company/intelimed-ai/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-bold opacity-80 uppercase tracking-widest hover:text-secondary transition-colors"
                  >
                    Intelimed
                  </a>
                </div>
              </div>
            </div>

            {/* Right form box */}
            <div className="bg-surface border border-border rounded-[2.5rem] p-8 md:p-14 shadow-sm">
              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center text-center gap-6 py-10">
                  <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center">
                    <span className="material-symbols-outlined text-3xl text-secondary" aria-hidden="true">check_circle</span>
                  </div>
                  <div>
                    <h3 className="font-headline text-2xl font-extrabold text-primary mb-2">{t.success.title}</h3>
                    <p className="opacity-60 text-sm">{t.success.message}</p>
                  </div>
                  <button
                    className="text-xs font-bold uppercase tracking-widest text-secondary hover:text-primary transition-colors"
                    onClick={() => setStatus('idle')}
                  >
                    {language === 'es' ? 'Enviar otro mensaje' : 'Send another message'}
                  </button>
                </div>
              ) : (
                <form className="space-y-6" onSubmit={handleSubmit} noValidate>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="contact-name" className="block text-[10px] font-bold text-primary uppercase tracking-wider mb-2">
                        {t.form.name}
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={fieldClass('name')}
                        placeholder={t.form.namePlaceholder}
                      />
                      {errors.name && touched.name && (
                        <p className="text-xs text-red-400 mt-1">{errors.name}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="contact-institution" className="block text-[10px] font-bold text-primary uppercase tracking-wider mb-2">
                        {t.form.institution}
                      </label>
                      <input
                        id="contact-institution"
                        name="institution"
                        type="text"
                        value={formData.institution}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={fieldClass('institution')}
                        placeholder={t.form.institutionPlaceholder}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-[10px] font-bold text-primary uppercase tracking-wider mb-2">
                      {t.form.email}
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={fieldClass('email')}
                      placeholder={t.form.emailPlaceholder}
                    />
                    {errors.email && touched.email && (
                      <p className="text-xs text-red-400 mt-1">{errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-[10px] font-bold text-primary uppercase tracking-wider mb-2">
                      {t.form.message}
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows="4"
                      value={formData.message}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={fieldClass('message')}
                      placeholder={t.form.messagePlaceholder}
                    />
                    {errors.message && touched.message && (
                      <p className="text-xs text-red-400 mt-1">{errors.message}</p>
                    )}
                  </div>

                  {errors.submit && (
                    <p className="text-xs text-red-400">{errors.submit}</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full bg-primary text-on-primary font-headline font-black py-5 rounded-xl text-[10px] uppercase tracking-[0.2em] hover:bg-secondary hover:text-on-secondary transition-all active:scale-95 shadow-xl shadow-secondary/5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {status === 'submitting' ? t.form.submitting : t.form.submit}
                  </button>
                </form>
              )}
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
